import type { UIMessage } from 'ai';
import type { MemoryRecallBadgePayload } from '../_components/rich/memory-recall-badge';
import { type ToolPartData, asToolPart } from './tool-parts';

/**
 * Sort a message's parts into the buckets each surface renders separately
 * (text, tool cards, reasoning, sources, images, memory-recall badges).
 *
 * ONE DEFINITION for both chat surfaces (desktop route + mobile Ask AI
 * sheet) — this was a byte-for-byte duplicate across their two
 * `message-list.tsx` files, and the copies had already drifted: the desktop
 * route's `sourceParts` never validated `src.url` before using it as an
 * `href` (the mobile copy restricted it to `/^https?:/i`), so a source part
 * carrying something like `javascript:...` — the model controls this field
 * — would only have been neutralised on one of the two surfaces. Sanitizing
 * once, here, means both consumers get a safe url or none.
 */

export interface ReasoningPartLike {
  type: string;
  text?: string;
  state?: string;
}

export interface SourcePartLike {
  type: string;
  /** Already validated to be an explicit http(s) URL, or absent. */
  url?: string;
  title?: string;
}

export interface FilePartLike {
  type: 'file';
  url: string;
  mediaType: string;
  filename?: string;
}

export interface BucketedParts {
  textParts: UIMessage['parts'];
  toolParts: ToolPartData[];
  reasoningParts: ReasoningPartLike[];
  sourceParts: SourcePartLike[];
  fileParts: FilePartLike[];
  memoryRecallParts: MemoryRecallBadgePayload[];
}

/**
 * Parse a `data-memory_recalled` part into a badge payload, or return null
 * if the shape doesn't match. The emit side (`plan-chat-turn.ts`'s `run()`)
 * shipped in Phase 3 of docs/plans/claire-memory-suggestions.md — one
 * `data-memory_recalled` event per `preference` memory `queryKnowledge`
 * judged relevant to the turn's message, emitted right after `start`.
 * Shape-checking stays regardless, since a wire part is untrusted input.
 */
function asMemoryRecallPart(
  part: UIMessage['parts'][number]
): MemoryRecallBadgePayload | null {
  if (part.type !== 'data-memory_recalled') return null;
  const data = (part as unknown as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return null;
  const { knowledgeEntryId, content, scope } = data as {
    knowledgeEntryId?: unknown;
    content?: unknown;
    scope?: unknown;
  };
  if (typeof knowledgeEntryId !== 'string' || knowledgeEntryId.length === 0) {
    return null;
  }
  if (typeof content !== 'string' || content.length === 0) return null;
  if (scope !== 'personal' && scope !== 'organization') return null;
  return { knowledgeEntryId, content, scope };
}

/**
 * The model controls a source part's `url` — only treat it as a safe `href`
 * when it's an explicit http(s) URL, otherwise drop it so callers fall back
 * to rendering the title/url as plain text rather than a clickable link.
 */
function safeSourceUrl(url: unknown): string | undefined {
  return typeof url === 'string' && /^https?:/i.test(url) ? url : undefined;
}

export function bucketParts(parts: UIMessage['parts']): BucketedParts {
  const textParts: UIMessage['parts'] = [];
  const toolParts: ToolPartData[] = [];
  const reasoningParts: ReasoningPartLike[] = [];
  const sourceParts: SourcePartLike[] = [];
  const fileParts: FilePartLike[] = [];
  const memoryRecallParts: MemoryRecallBadgePayload[] = [];

  for (const part of parts) {
    if (part.type === 'text') {
      textParts.push(part);
      continue;
    }

    const tp = asToolPart(part);
    if (tp) {
      toolParts.push(tp);
      continue;
    }

    // C-02 may emit these once the new wire format ships.
    if (part.type === 'reasoning') {
      reasoningParts.push(part as unknown as ReasoningPartLike);
      continue;
    }

    if (part.type === 'source-url' || part.type === 'source-document') {
      const raw = part as unknown as { url?: unknown; title?: unknown };
      sourceParts.push({
        type: part.type,
        url: safeSourceUrl(raw.url),
        title: typeof raw.title === 'string' ? raw.title : undefined,
      });
      continue;
    }

    // Memory recall — live as of Phase 3, docs/plans/claire-memory-suggestions.md.
    // See `rich/memory-recall-badge.tsx` for the badge itself.
    const memoryRecall = asMemoryRecallPart(part);
    if (memoryRecall) {
      memoryRecallParts.push(memoryRecall);
      continue;
    }

    // Image file parts (W-C11). Only render when the part has both a URL
    // and an image/* mediaType — defensive against partially-formed parts
    // (e.g., during stream-in or from a future provider).
    if (part.type === 'file') {
      const fp = part as unknown as {
        url?: string;
        mediaType?: string;
        filename?: string;
      };
      if (
        typeof fp.url === 'string' &&
        typeof fp.mediaType === 'string' &&
        fp.mediaType.startsWith('image/')
      ) {
        fileParts.push({
          type: 'file',
          url: fp.url,
          mediaType: fp.mediaType,
          filename: fp.filename,
        });
      }
    }
  }

  return {
    textParts,
    toolParts,
    reasoningParts,
    sourceParts,
    fileParts,
    memoryRecallParts,
  };
}

export function joinReasoning(parts: ReasoningPartLike[]): string {
  return parts.map((p) => p.text ?? '').join('');
}

export function isStreamingReasoning(p: ReasoningPartLike): boolean {
  return p.state === 'streaming';
}
